<script setup>
import { computed } from 'vue';
import { describeRule, formatEndsAt, isNoList } from '@/services/pricingRules';

// Details behind the small rule marker on a cart line: which rule was selected, why
// (priority, price list, stock threshold), and every other candidate that was considered.
const props = defineProps({
  item: { type: Object, required: true },
  explanation: { type: Object, required: true },
});
const emit = defineEmits(['close']);

const applied = computed(() => props.item?.appliedPricingRule || null);
const match = computed(() => props.explanation?.match || null);
const ctx = computed(() => props.explanation?.context || null);

const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;
const listLabel = (l) => (isNoList(l) ? 'all price lists' : l);
const when = (iso) => {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const kindLabel = (k) => ({ Variant: 'Item code', Category: 'Item group', Brand: 'Brand', Product: 'Product code' }[k] || k);
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40" @click.self="emit('close')">
      <div class="w-[560px] max-w-[95vw] max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-xl p-5 text-sm" @click.stop>
        <div class="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 class="text-base font-semibold">Pricing rule details</h3>
            <p class="text-xs text-gray-500">{{ item.ItemName }} · {{ item.ItemId || item.Sku }}</p>
          </div>
          <button type="button" class="pi pi-times text-gray-500 hover:text-black p-1" @click="emit('close')" aria-label="Close"></button>
        </div>

        <!-- Selected rule -->
        <div v-if="match" class="rounded-xl border border-green-200 bg-green-50 p-3 mb-3">
          <div class="flex items-center justify-between">
            <span class="font-semibold text-green-800">
              <i class="pi pi-tag mr-1" style="font-size: 11px;"></i>{{ match.externalId }}
            </span>
            <span class="text-xs px-2 py-0.5 rounded-full" :class="applied ? 'bg-green-600 text-white' : 'bg-white text-green-800 border border-green-300'">
              {{ applied ? 'applied to this line' : 'available' }}
            </span>
          </div>
          <dl class="grid grid-cols-[130px_1fr] gap-x-3 gap-y-1 mt-2 text-xs">
            <dt class="text-gray-500">Discount</dt><dd>{{ describeRule(match) }} → {{ money(match.salePrice) }} (from {{ money(ctx?.price) }})</dd>
            <dt class="text-gray-500">Priority</dt><dd>{{ match.priority }}</dd>
            <dt class="text-gray-500">Rule price list</dt><dd>{{ listLabel(match.priceList) }}</dd>
            <dt class="text-gray-500">Item priced from</dt><dd>{{ listLabel(ctx?.priceList) }}</dd>
            <dt class="text-gray-500">Matched on</dt><dd>{{ kindLabel(match.appliesTo) }} "{{ match.selector }}"</dd>
            <dt class="text-gray-500">Valid</dt><dd>{{ when(match.startsAt) }} → {{ formatEndsAt(match.endsAt) }}</dd>
          </dl>
        </div>
        <div v-else class="rounded-xl border border-gray-200 bg-gray-50 p-3 mb-3 text-xs text-gray-600">
          <template v-if="!explanation.eligible">This line type never gets pricing rules.</template>
          <template v-else-if="!explanation.product">The product could not be resolved in the catalogue or the scan cache, so no rule can be evaluated.</template>
          <template v-else>No pricing rule currently applies to this item.</template>
        </div>

        <!-- Applied but stale -->
        <div v-if="applied && (!match || match.externalId !== applied.externalId || Math.abs(Number(match.salePrice) - Number(applied.rulePrice)) >= 0.005)"
             class="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-3 text-xs text-amber-800">
          Applied rule {{ applied.externalId }} ({{ describeRule(applied) }} → {{ money(applied.rulePrice) }}) is no longer what the current rules would select. The line keeps its price until you revert.
        </div>

        <!-- Product as evaluated -->
        <div v-if="ctx" class="mb-3">
          <h4 class="text-xs font-semibold text-gray-700 mb-1">Item as evaluated</h4>
          <dl class="grid grid-cols-[130px_1fr] gap-x-3 gap-y-1 text-xs">
            <dt class="text-gray-500">Item code</dt><dd>{{ ctx.sku }}</dd>
            <dt class="text-gray-500">Brand</dt><dd>{{ ctx.brand || '—' }}</dd>
            <dt class="text-gray-500">Item groups</dt><dd>{{ (ctx.categories && ctx.categories.length) ? ctx.categories.join(', ') : '—' }}</dd>
            <dt class="text-gray-500">Price list</dt><dd>{{ listLabel(ctx.priceList) }} <span class="text-gray-400">(branch: {{ explanation.branchLists.join(', ') || 'none' }})</span></dd>
            <dt class="text-gray-500">Stock on hand</dt><dd>{{ ctx.qoh === null || ctx.qoh === undefined ? 'unknown (threshold not checked)' : ctx.qoh }}</dd>
          </dl>
        </div>

        <!-- Candidates -->
        <div>
          <h4 class="text-xs font-semibold text-gray-700 mb-1">
            Rules considered <span class="font-normal text-gray-400">({{ explanation.candidates.length }} of {{ explanation.rulesOnFile }} on this terminal)</span>
          </h4>
          <p v-if="!explanation.candidates.length" class="text-xs text-gray-500">No active rule on the branch's price lists targets this item.</p>
          <table v-else class="w-full text-xs">
            <thead>
              <tr class="text-left text-gray-500 border-b">
                <th class="py-1 pr-2 font-medium">Rule</th>
                <th class="py-1 pr-2 font-medium">Matched on</th>
                <th class="py-1 pr-2 font-medium text-right">Priority</th>
                <th class="py-1 pr-2 font-medium text-right">Min stock</th>
                <th class="py-1 font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in explanation.candidates" :key="c.externalId + '|' + c.selector + i" class="border-b border-gray-100" :class="c.selected ? 'bg-green-50' : ''">
                <td class="py-1 pr-2 font-medium">{{ c.externalId }}</td>
                <td class="py-1 pr-2">{{ kindLabel(c.appliesTo) }} "{{ c.selector }}"</td>
                <td class="py-1 pr-2 text-right">{{ c.priority }}</td>
                <td class="py-1 pr-2 text-right">{{ c.minQoh || 0 }}</td>
                <td class="py-1">
                  <span v-if="c.selected" class="text-green-700 font-semibold">selected</span>
                  <span v-else class="text-gray-500">{{ c.reason }}</span>
                </td>
              </tr>
            </tbody>
          </table>
          <p class="text-[11px] text-gray-400 mt-2">Highest priority wins; equal priorities go to the most recently received rule. One rule only, applied to the original price.</p>
        </div>
      </div>
    </div>
  </Teleport>
</template>
